// Home: the greeting, the notices, the stat cards and the widgets.
import { plural } from "../../helpers";

const p = plural("en");

export default {
  greeting: { morning: "Good morning", afternoon: "Good afternoon", evening: "Good evening" },
  checking: "Checking what the agents are doing…",
  noAgents: "No agents are running. Start a run from a workflow, or wait for a trigger.",
  working: (n: number) => p(n, { one: "# agent is working.", other: "# agents are working." }),
  paused: (n: number) => p(n, { one: "One is paused on a question only you can answer.", other: "# are paused on a question only you can answer." }),
  linkDevice: "Link a device",
  startRun: "Start a run",
  activeRuns: "Active runs",
  runsInfo: (working: number, waiting: number) => `${working} running · ${waiting} waiting for you`,
  loadingRuns: "Loading runs",
  openQuestions: "Open questions",
  oldestAsked: (ago: string) => `Oldest asked ${ago}`,
  nothingWaiting: "Nothing waiting for you",
  spend30: "Model spend, 30 days",
  spendInfo: (today: string, runs: number) => `${today} today · ${p(runs, { one: "# run", other: "# runs" })}`,
  notReported: "Not reported by this host yet",
  calls24: "Broker calls, 24 h",
  deniedCount: (n: number) => `${n} denied by policy`,
  questions: "Questions",
  noneWaiting: "No agent is waiting for you",
  noneWaitingBody: "When an agent asks a question, it appears here and on your phone.",
  aRun: "A run",
  needsAnswer: (name: string) => `${name} needs an answer`,
  asked: (ago: string) => `asked ${ago} · worker checkpointed, no CPU held`,
  questionEncrypted: "The question is encrypted to your devices. Open the run to read it and answer from this browser.",
  openConversation: "Open the conversation",
  moreWaiting: (n: number) => `${n} more waiting`,
  answerNote: "Your answer is encrypted in this browser and signed with its device key. The host relays it but cannot read it.",
  deviceWaiting: "Device waiting for approval",
  wantsToLink: (name: string) => `${name} wants to link`,
  checkFingerprint: "Check its fingerprint first",
  review: "Review",
  credentialsStay: "Credentials stay on the host.",
  brokerActs: "Workers ask the broker to act for them; they never see a secret.",
  callsMade: (calls: number, denied: number) =>
    `Workers made ${p(calls, { one: "# broker call", other: "# broker calls" })} in the last day${denied > 0 ? `; ${denied} denied by policy` : ""}. None of them saw a secret.`,
  noOrg: "No organisation yet",
  capacity: {
    title: "Host capacity",
    incus: (version: string) => `Incus ${version}`,
    up: (uptime: string) => `up ${uptime}`,
    cpu: "CPU",
    memory: "Memory",
    disk: "Disk",
    cores: "cores",
    cpuDetail: (pct: string, reserved: number) => `${pct} · quotas reserve ${reserved} cores`,
    reserve: (gib: number) => `Quotas reserve ${gib} GiB`,
    chartFills: "The chart fills in as ocs-api samples the host.",
    empty: {
      title: "No host capacity yet",
      missing: "This host's ocs-api does not report host capacity yet.",
      failed: (error: string) => `Could not load host capacity: ${error}`,
    },
  },
  spend: {
    title: "Model spend",
    description: "Last 30 days · via the broker",
    noneBody: "Runs record their cost when they finish.",
    label: "Spend",
    empty: {
      title: "No model spend yet",
      missing: "This host's ocs-api does not report model spend yet.",
      failed: (error: string) => `Could not load model spend: ${error}`,
    },
  },
  calls: {
    title: "Recent broker calls",
    description: "Every call a worker made with a credential it never saw",
    tableLabel: "Broker calls",
    time: "Time",
    action: "Action",
    target: "Target",
    secretUsed: "Secret used",
    run: "Run",
    outcome: "Outcome",
    latency: "Latency",
    outcomes: { allowed: "Allowed", approved: "Approved on a device", denied: "Denied by policy" },
    noneBody: "Calls appear here when a worker uses a secret through the broker.",
    empty: {
      title: "No broker calls yet",
      missing: "This host's ocs-api does not report broker calls yet.",
      failed: (error: string) => `Could not load broker calls: ${error}`,
    },
  },
  environments: {
    title: "Environments",
    all: "All",
    egressRules: "egress rules",
    none: "No environments yet",
    noneBody: "An environment is an isolated Incus instance with its own network, VPN and Docker.",
  },
  runs: {
    title: "Runs needing attention",
    all: "All runs",
    none: "No runs yet",
    noneBody: "A run starts when a workflow's trigger fires or you start one.",
    run: "Run",
  },
};
